import { Component, computed, input } from '@angular/core'
import type { Notification } from '@awork/framework/models/notification.model'
import { NotificationEvents } from '@awork/framework/models/notification.types'
import { DateTimePipe } from '@awork/_shared/pipes/date-time.pipe'
import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import { TaskCheckboxComponent } from '../../../features/tasks/components/task-checkbox/task-checkbox.component'
import { ProjectAvatarComponent } from '../../../features/projects/components/project-avatar/project-avatar.component'
import { UserAvatarComponent } from '../../../features/user/components/user-avatar/user-avatar.component'

/** Notification details shared by the inbox row and incoming toast. */
@Component({
  selector: 'aw-notification-content',
  templateUrl: './notification-content.component.html',
  styleUrl: './notification-content.component.scss',
  imports: [DateTimePipe, IconsDirective, TaskCheckboxComponent, ProjectAvatarComponent, UserAvatarComponent]
})
export class NotificationContentComponent {
  readonly notification = input.required<Notification>()
  readonly author = computed(() => this.notification().responsibleUser || this.notification().userInNotification)
  readonly authorName = computed(
    () => this.notification().responsibleUser?.shortName || this.notification().userInNotification?.shortName || ''
  )

  readonly notificationDescription = computed(() => this.notification().description)
  readonly notificationTitle = computed(() => this.notification().entityName || this.notification().text)
  readonly activityLabel = computed(() => this.getActivityLabel())
  readonly isDueDate = computed(() => this.notification().groupType === 'deadlines')
  protected readonly translations = q.translations.Notifications

  /**
   * Keeps the action separate from the item name and author in the mobile list.
   * @withSignal notification Current notification.
   * @returns {string} Short activity label, or an empty string to use the full description.
   */
  private getActivityLabel(): string {
    const labels = this.translations.activities

    switch (this.notification().event) {
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.ProjectAssigned:
        return labels.assigned
      case NotificationEvents.TaskUnassigned:
        return labels.unassigned
      case NotificationEvents.TaskCommented:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.DocumentCommented:
        return labels.comments
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.DocumentMentioned:
      case NotificationEvents.DocumentCommentMentioned:
        return labels.mentions
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.ProjectStatusChanged:
        return labels.statusUpdates
      case NotificationEvents.DocumentContributorAdded:
      case NotificationEvents.DocumentSpaceContributorAdded:
      case NotificationEvents.FilterContributorAdded:
      case NotificationEvents.AgentShared:
        return labels.shared
      default:
        return ''
    }
  }
}
